import { AfterViewInit, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { PaginationInfo } from 'src/app/shared/models';
import { VehicleInspectionService } from 'src/app/services/inpection/vehicle-inspection.service';
import { InspectionManagerModalService } from 'src/app/services/inspection-manager-modal/inspection-manager-modal.service';
import { VendorService } from 'src/app/services/vendor/vendor.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import Swal from 'sweetalert2';

declare var bootstrap: any;
declare var KTMenu: any;

@Component({
  selector: 'app-inspection-manager',
  templateUrl: './inspection-manager.component.html',
  styleUrls: ['./inspection-manager.component.scss']
})
export class InspectionManagerComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Data properties
  inspections: any[] = [];
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // State properties
  isLoading: boolean = false;
  isSendingBulkMail: boolean = false;
  searchTerm: string = '';
  currentPage: number = 1;
  showFiltersForm = false;

  // Selection properties
  selectedInspections: Set<string> = new Set();
  selectAll: boolean = false;

  // Filter properties
  filters = {
    insp_apply_date_from: '',
    insp_apply_date_to: '',
    insp_passed_date_from: '',
    insp_passed_date_to: '',
    insp_agency_id: '',
    insp_status: '',
    search: ''
  };

  // Inspection Agency search properties
  inspAgencySearchTerm: string = '';
  inspAgencySearchResults: any[] = [];
  selectedInspAgency: any = null;
  showInspAgencyDropdown: boolean = false;
  isInspAgencySearching: boolean = false;
  private inspAgencySearchSubject = new Subject<string>();

  constructor(
    private _vehicleInspectionService: VehicleInspectionService,
    private _inspectionManagerModalService: InspectionManagerModalService,
    private _vendorService: VendorService,
    private cdr: ChangeDetectorRef
  ) {
    super();
    this.setupInspAgencySearchDebounce();
  }

  ngOnInit(): void {
    this.loadInspections();
    this.setupModalSubscriptions();
  }

  ngAfterViewInit(): void {
    this.initializeMenus();
  }

  private setupModalSubscriptions(): void {
    // Subscribe to modal service changes if needed
    this.subscriptions.push(
      this._inspectionManagerModalService.selectedInspection$.subscribe(() => {
        // Handle inspection selection changes if needed
      })
    );
  }

  getVehicleImageUrl(vehicle: any): string {
    if (vehicle.pictures && vehicle.pictures.length > 0) {
      return vehicle.pictures[0];
    }
    return 'assets/media/svg/files/blank-image.svg'; // Default placeholder image
  }

  private loadInspections(page: number = 1, search: string = '', filters: any = {}): void {
    this.isLoading = true;
    this.currentPage = page;
    delete filters.search;
    Object.keys(filters).forEach(key => {
      if (filters[key] === null || filters[key] === undefined || filters[key] === '') {
        delete filters[key];
      }
    });

    // Clear selections when loading new data
    this.selectedInspections.clear();
    this.selectAll = false;

    this.subscriptions.push(
      this._vehicleInspectionService.GetData(page.toString(), search, filters).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status && response.data) {
            this.inspections = response.data.data || [];
            this.updatePaginationInfo(response.data);
          } else {
            this.inspections = [];
            this.resetPagination();
          }
          this.cdr.detectChanges();
          this.initializeMenus();
        },
        error: (error: any) => {
          this.isLoading = false;
          console.error('Error loading inspections:', error);
          this.inspections = [];
          this.resetPagination();
          this.cdr.detectChanges();
        }
      })
    );
  }

  private updatePaginationInfo(data: any): void {
    this.paginationInfo = {
      currentPage: data.current_page || 1,
      perPage: data.per_page || 20,
      total: data.total || 0,
      lastPage: data.last_page || 1,
      firstPage: 1
    };
  }

  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 20,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  private initializeMenus(): void {
    setTimeout(() => {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.createInstances();
      }
    }, 100);
  }

  // Search and filter methods
  onSearch(): void {
    this.currentPage = 1;
    this.loadInspections(this.currentPage, this.searchTerm, this.filters);
  }

  onPageChange(page: number): void {
    this.loadInspections(page, this.searchTerm, this.filters);
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.loadInspections(this.currentPage, this.searchTerm, this.filters);
  }

  clearFilters(): void {
    this.filters = {
      insp_apply_date_from: '',
      insp_apply_date_to: '',
      insp_passed_date_from: '',
      insp_passed_date_to: '',
      insp_agency_id: '',
      insp_status: '',
      search: ''
    };
    this.selectedInspAgency = null;
    this.inspAgencySearchTerm = '';
    this.inspAgencySearchResults = [];
    this.showInspAgencyDropdown = false;
    this.currentPage = 1;
    this.loadInspections();
  }

  // Inspection Agency search methods
  private setupInspAgencySearchDebounce(): void {
    this.inspAgencySearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.searchInspAgencies(searchTerm);
    });
  }

  onInspAgencySearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.inspAgencySearchTerm = target.value;
    this.selectedInspAgency = null;
    this.filters.insp_agency_id = '';

    if (this.inspAgencySearchTerm.length >= 2) {
      this.inspAgencySearchSubject.next(this.inspAgencySearchTerm);
      this.showInspAgencyDropdown = true;
    } else {
      this.showInspAgencyDropdown = false;
      this.inspAgencySearchResults = [];
    }
  }

  onInspAgencyInputFocus(): void {
    if (this.inspAgencySearchResults.length > 0) {
      this.showInspAgencyDropdown = true;
    }
  }

  onInspAgencyInputBlur(): void {
    setTimeout(() => {
      this.showInspAgencyDropdown = false;
    }, 200);
  }

  private searchInspAgencies(searchTerm: string): void {
    if (!searchTerm || searchTerm.length < 2) {
      this.inspAgencySearchResults = [];
      this.showInspAgencyDropdown = false;
      return;
    }

    this.isInspAgencySearching = true;
    this.subscriptions.push(
      this._vendorService.GetData('1', searchTerm, { paginate: false, type: 'inspection_agency' }).subscribe({
        next: (response: any) => {
          this.isInspAgencySearching = false;
          if (response.status && response.data) {
            this.inspAgencySearchResults = response.data.data || response.data || [];
            this.showInspAgencyDropdown = this.inspAgencySearchResults.length > 0;
          } else {
            this.inspAgencySearchResults = [];
            this.showInspAgencyDropdown = false;
          }
        },
        error: (error: any) => {
          this.isInspAgencySearching = false;
          console.error('Error searching inspection agencies:', error);
          this.inspAgencySearchResults = [];
          this.showInspAgencyDropdown = false;
        }
      })
    );
  }

  selectInspAgency(agency: any): void {
    this.selectedInspAgency = agency;
    this.inspAgencySearchTerm = this.getInspAgencyDisplayName(agency);
    this.filters.insp_agency_id = agency._id || agency.id;
    this.showInspAgencyDropdown = false;
  }

  private getInspAgencyDisplayName(agency: any): string {
    return agency.name || agency.company_name || 'Unknown Agency';
  }

  // Modal methods
  openAddModal(): void {
    this._inspectionManagerModalService.clearSelectedInspection();
    this._inspectionManagerModalService.clearPreSelectedVehicle();

    // Use setTimeout to ensure DOM is ready
    setTimeout(() => {
      const modalElement = document.getElementById('kt_modal_add_edit_inspection_manager');

      if (modalElement) {
        // Check if modal instance already exists
        let modal = bootstrap.Modal.getInstance(modalElement);
        if (!modal) {
          modal = new bootstrap.Modal(modalElement);
        }
        modal.show();
      } else {
        console.error('Modal element not found');
      }
    }, 100);
  }

  openEditModal(inspection: any): void {
    this._inspectionManagerModalService.setSelectedInspection(inspection);

    // Use setTimeout to ensure DOM is ready
    setTimeout(() => {
      const modalElement = document.getElementById('kt_modal_add_edit_inspection_manager');

      if (modalElement) {
        // Check if modal instance already exists
        let modal = bootstrap.Modal.getInstance(modalElement);
        if (!modal) {
          modal = new bootstrap.Modal(modalElement);
        }
        modal.show();
      } else {
        console.error('Modal element not found');
      }
    }, 100);
  }

  deleteInspection(inspection: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You won\'t be able to revert this!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(inspection);
      }
    });
  }

  private performDelete(inspection: any): void {
    const inspectionId = inspection._id || inspection.id;
    this.subscriptions.push(
      this._vehicleInspectionService.DeleteDataById(inspectionId).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Inspection record has been deleted.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.loadInspections(this.currentPage, this.searchTerm, this.filters);
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete inspection record.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          console.error('Error deleting inspection:', error);
          Swal.fire({
            title: 'Error!',
            text: 'An error occurred while deleting the inspection record.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  // Selection methods
  toggleSelectAll(): void {
    this.selectAll = !this.selectAll;
    if (this.selectAll) {
      this.inspections.forEach(inspection => {
        this.selectedInspections.add(inspection._id || inspection.id);
      });
    } else {
      this.selectedInspections.clear();
    }
  }

  toggleInspectionSelection(inspectionId: string): void {
    if (this.selectedInspections.has(inspectionId)) {
      this.selectedInspections.delete(inspectionId);
    } else {
      this.selectedInspections.add(inspectionId);
    }

    // Update select all checkbox state
    this.selectAll = this.selectedInspections.size === this.inspections.length;
  }

  isInspectionSelected(inspectionId: string): boolean {
    return this.selectedInspections.has(inspectionId);
  }

  get hasSelectedInspections(): boolean {
    return this.selectedInspections.size > 0;
  }

  // Send mail methods
  sendSingleMail(inspection: any): void {
    const inspectionId = inspection._id || inspection.id;
    this.sendMail([inspectionId]);
  }

  sendBulkMail(): void {
    if (this.selectedInspections.size === 0) {
      Swal.fire({
        title: 'No Selection',
        text: 'Please select at least one inspection to send mail.',
        icon: 'warning',
        confirmButtonText: 'OK'
      });
      return;
    }

    // Get selected inspection records
    const selectedIds = Array.from(this.selectedInspections);
    const selectedInspectionRecords = this.inspections.filter(inspection =>
      selectedIds.includes(inspection._id || inspection.id)
    );

    // Validate that all selected inspections have the same insp_agency_id
    const inspAgencyIds = selectedInspectionRecords.map(inspection =>
      inspection.insp_agency_id || inspection.insp_agency?._id || inspection.insp_agency?.id
    );
    const uniqueInspAgencyIds = [...new Set(inspAgencyIds.filter(id => id))];

    if (uniqueInspAgencyIds.length > 1) {
      Swal.fire({
        title: 'Validation Error',
        text: 'All selected inspections must have the same Inspection Agency.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      return;
    }

    if (uniqueInspAgencyIds.length === 0) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Selected inspections must have an Inspection Agency assigned.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      return;
    }

    // Validate that all selected inspections have the same vehicle.yard_id
    const yardIds = selectedInspectionRecords.map(inspection =>
      inspection.vehicle?.yard_id
    );
    const uniqueYardIds = [...new Set(yardIds.filter(id => id))];

    if (uniqueYardIds.length > 1) {
      Swal.fire({
        title: 'Validation Error',
        text: 'All selected inspections must have vehicles from the same Yard.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      return;
    }

    if (uniqueYardIds.length === 0) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Selected inspections must have vehicles with a Yard assigned.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      return;
    }

    // All validations passed, proceed with sending mail
    this.sendMail(selectedIds);
  }

  private sendMail(inspectionIds: string[]): void {
    const data = {
      inspection_ids: inspectionIds
    };

    // Set loading state for bulk mail
    if (inspectionIds.length > 1) {
      this.isSendingBulkMail = true;
    }

    this.subscriptions.push(
      this._vehicleInspectionService.SendMail(data).subscribe({
        next: (response: any) => {
          this.isSendingBulkMail = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: `Mail sent successfully for ${inspectionIds.length} inspection(s).`,
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              // Clear selections after successful send
              this.selectedInspections.clear();
              this.selectAll = false;
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to send mail.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          this.isSendingBulkMail = false;
          console.error('Error sending mail:', error);
          Swal.fire({
            title: 'Error!',
            text: 'An error occurred while sending mail.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  // Event handlers for modal events
  onInspectionAdded(): void {
    this.loadInspections(this.currentPage, this.searchTerm, this.filters);
  }

  onInspectionUpdated(): void {
    this.loadInspections(this.currentPage, this.searchTerm, this.filters);
  }

  // Track by function for ngFor performance
  trackByFn(index: number, item: any): any {
    return item._id || item.id || index;
  }
}
